import { createContext, useContext, useState, type ReactNode } from 'react';
import { useCreatorPortalData } from '../hooks/useCreatorPortalData';
import type { Influencer } from '../types/influencer';
import type { CreatorCampaignData } from '../services/creatorPortalService';

interface CreatorPortalContextType {
  loading: boolean;
  refreshing: boolean;
  error: string | null;
  viewingAs: boolean;
  influencer: Influencer | null;
  campaigns: CreatorCampaignData[];
  activeCampaigns: CreatorCampaignData[];
  completedCampaigns: CreatorCampaignData[];
  consultationCampaigns: CreatorCampaignData[];
  billingCampaigns: CreatorCampaignData[];
  awaitingClientPaymentCampaigns: CreatorCampaignData[];
  pipelineCampaigns: CreatorCampaignData[];
  isRealtimeConnected: boolean;
  realtimeError: string | null;
  lastSyncedAt: Date | null;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
  loadData: () => Promise<void>;
  selectedCampaignId: string | null;
  setSelectedCampaignId: (id: string | null) => void;
}

const CreatorPortalContext = createContext<CreatorPortalContextType | null>(null);

export function CreatorPortalProvider({ children }: { children: ReactNode }) {
  const data = useCreatorPortalData();
  const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(null);

  return (
    <CreatorPortalContext.Provider value={{ ...data, selectedCampaignId, setSelectedCampaignId }}>
      {children}
    </CreatorPortalContext.Provider>
  );
}

export function useCreatorPortal() {
  const ctx = useContext(CreatorPortalContext);
  if (!ctx) throw new Error('useCreatorPortal must be used within CreatorPortalProvider');
  return ctx;
}
